import {
  HashRouter,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import UserDataProvider from "@/hooks/UserDataProvider";
import { MainLayout } from "@/layouts/Main_Layout";
import { LoaderProvider } from "@/hooks/LoaderProvider";
import GlobalErrorHandler from "@/hooks/GlobalErrorHandler";
import EmailSignIn from "@/features/auth/sign_in/Email_Sign_In";
import PasswordSignIn from "@/features/auth/sign_in/Password_Sign_In";
import TwoFA from "@/features/auth/2FA/2FA";
import SignUp from "@/features/auth/sign_up/Sign_Up";
import Otp from "@/features/auth/otp/Otp";
import Guest from "@/features/invites/Guest";
import Member from "@/features/invites/Member";
import EmailRecovery from "@/features/account_recovery/Email_Recovery";
import PasswordRecovery from "@/features/account_recovery/Password_Recovery";
import PasswordReset from "@/features/password_reset/Password_Reset";
import AccountSetupStep1 from "@/features/account_&_kyc_setup/Account_Setup_Step_1";
import AccountSetupStep2 from "@/features/account_&_kyc_setup/Account_Setup_Step_2";
import AccountSetupStep3 from "@/features/account_&_kyc_setup/Account_Setup_Step_3";
import ReviewAndSubmit from "@/features/account_&_kyc_setup/Review_&_Submit";
import AccountSetupSuccess from "@/features/account_&_kyc_setup/Account_Setup_Success";
import Subscription from "@/features/subscription/Subscription";
import ProceedToPaymentGateway from "@/features/subscription/Proceed_To_Payment_Gateway";
import PaymentSuccess from "@/features/subscription/Payment_Success";
import ChatHome from "@/pages/chat/Chat_Home";
import SingleChat from "@/pages/chat/Single_Chat";
import "@/App.css";
import AuthLayout from "@/features/auth/layout/AuthLayout";
import AccountSetupLayout from "@/features/account_&_kyc_setup/layout/AccountSetupLayout";
import Polls from "@/pages/admin/polls/Polls";
import { ThemeProvider } from "@/hooks/ThemeContext";
import { LoadScript } from "@react-google-maps/api";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "@/store/store";
import NetworkStatusProvider from "@/hooks/NetworkStatusProvider";
import OfflineCacheManager from "@/hooks/offlineCacheManager";
import {
  setupPeriodicSaving,
  loadDataFromStorage,
} from "@/services/persistent_storage";
import { useEffect, useState } from "react";
import {
  setAuthenticated,
  updateUserInfo,
  setToken,
} from "@/store/redux/slices/auth/auth_slice";
import PropTypes from "prop-types";
import axios from "axios";

// Create a Protected Route component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (typeof window !== "undefined" && window.IS_ELECTRON === true) {
      const checkAuth = async () => {
        if (!isAuthenticated) {
          try {
            // First check if we have the token directly
            const token = localStorage.getItem("access_token");

            if (token) {
              dispatch(setAuthenticated(true));
            } else {
              // Fall back to checking persisted data
              const data = await loadDataFromStorage();

              if (
                data &&
                data.auth &&
                (data.auth.isAuthenticated === true ||
                  data.auth.isAuthenticated === "true")
              ) {
                dispatch(setAuthenticated(true));

                if (data.auth.userInfo) {
                  const userInfo =
                    typeof data.auth.userInfo === "string"
                      ? JSON.parse(data.auth.userInfo)
                      : data.auth.userInfo;
                  dispatch(updateUserInfo(userInfo));
                }
              }
            }
          } catch (error) {
            console.error("Error checking persisted auth:", error);
          }
        }
        setIsLoading(false);
      };

      checkAuth();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, dispatch]);

  if (isLoading) {
    // Prevent redirect flicker while Electron auth state is being restored.
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node,
};

function AnimatedRoutes() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route element={<AuthLayout />}>
          <Route
            path="/signin"
            element={
              isAuthenticated ? <Navigate to="/dashboard" /> : <EmailSignIn />
            }
          />
          <Route
            path="/signin/password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <PasswordSignIn />
              )
            }
          />
          <Route path="/signup" element={<SignUp />} />

          {/* Add the invite routes under AuthLayout */}
          <Route
            path="/invite/member/:userId/:organizationId"
            element={<Member />}
          />
          <Route
            path="/invite/guest/:userId/:organizationId"
            element={<Guest />}
          />
        </Route>

        <Route path="/otp" element={<Otp />} />
        <Route path="/2fa/verify" element={<TwoFA />} />

        <Route element={<AccountSetupLayout />}>
          <Route
            path="/account-setup-step-1"
            element={
              <ProtectedRoute>
                <AccountSetupStep1 />
              </ProtectedRoute>
            }
          />
          <Route path="/account-setup-step-2" element={<AccountSetupStep2 />} />
          <Route path="/account-setup-step-3" element={<AccountSetupStep3 />} />
          <Route
            path="/account-setup-review-and-submit"
            element={<ReviewAndSubmit />}
          />
        </Route>
        <Route
          path="/account-setup-success"
          element={<AccountSetupSuccess />}
        />
        <Route path="/subscription" element={<Subscription />} />
        <Route
          path="/proceed-to-payment-gateway"
          element={<ProceedToPaymentGateway />}
        />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route
          path="/account-recovery/email-recovery"
          element={<EmailRecovery />}
        />
        <Route
          path="/account-recovery/password-recovery"
          element={<PasswordRecovery />}
        />
        <Route path="/password-reset" element={<PasswordReset />} />

        {/* Protected Chat Routes */}
        <Route
          path="/chat"
          element={
            <ChatHome />
            // <ProtectedRoute>
            // </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:id"
          element={
            <ProtectedRoute>
              <SingleChat />
            </ProtectedRoute>
          }
        />
        <Route path="/dynamic/polls/:id" element={<Polls />} />

        {/* Main Dashboard Routes */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        />

        {/* Default Route */}
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" />
            ) : (
              <Navigate to="/signin" />
            )
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const dispatch = useDispatch();
  const isOnline = useSelector((state) => state.network.isOnline);

  // Load persisted data including auth on app start in Electron
  useEffect(() => {
    const loadPersistedData = async () => {
      // Only run in Electron environment
      if (typeof window !== "undefined" && window.IS_ELECTRON === true) {
        const data = await loadDataFromStorage();

        // First, check if we have a token in localStorage directly
        const directToken = localStorage.getItem("access_token");
        if (directToken) {
          dispatch(setToken(directToken));
        }

        if (data && data.auth) {
          // Restore authentication state if it was persisted
          if (
            data.auth.isAuthenticated === true ||
            data.auth.isAuthenticated === "true"
          ) {
            dispatch(setAuthenticated(true));

            if (data.auth.token) {
              dispatch(setToken(data.auth.token));

              localStorage.setItem("access_token", data.auth.token);
            }

            if (data.auth.userInfo) {
              // If userInfo is a string (which can happen with Redux persist), parse it
              const userInfo =
                typeof data.auth.userInfo === "string"
                  ? JSON.parse(data.auth.userInfo)
                  : data.auth.userInfo;

              dispatch(updateUserInfo(userInfo));
            }
          }
        }
      }
    };

    loadPersistedData();
  }, [dispatch]);

  useEffect(() => {
    const cleanupSaving = setupPeriodicSaving();
    return cleanupSaving;
  }, []);

  // Setup axios interceptor to handle network errors gracefully
  useEffect(() => {
    // Add a request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Don't attempt network requests when offline
        if (!isOnline && config.url.includes("http")) {
          return Promise.reject(new Error("Network is offline"));
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor to handle network errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.message === "Network Error" && !isOnline) {
          // If we're in Electron, report the error to the main process
          if (
            typeof window !== "undefined" &&
            window.IS_ELECTRON === true &&
            window.electronAPI
          ) {
            window.electronAPI.reportError?.("dns-error");
          }

          return Promise.reject(new Error("Application is offline"));
        }
        return Promise.reject(error);
      }
    );

    // Clean up the interceptors
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isOnline]);

  return (
    <Provider store={store}>
      <PersistGate
        loading={<div style={{ display: "none" }}></div>}
        persistor={persistor}>
        <NetworkStatusProvider>
          <OfflineCacheManager>
            <ThemeProvider>
              {/* Render app content directly when in offline mode */}
              <AppContent />
            </ThemeProvider>
          </OfflineCacheManager>
        </NetworkStatusProvider>
      </PersistGate>
    </Provider>
  );
}

// Define libraries array outside component to prevent recreating on each render
const googleMapsLibraries = ["places"];

// Separate component for content that may or may not include Google Maps
function AppContent() {
  const isOnline = useSelector((state) => state.network.isOnline);

  // (Removed manual mapsLoaded management to avoid unmounting LoadScript after load)

  // Guard: prevent duplicate customElements.define for Google Maps Web Components (gmp-*)
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.customElements &&
      !window.__gmpDefineGuardInstalled
    ) {
      const originalDefine = window.customElements.define.bind(
        window.customElements
      );
      window.customElements.define = (name, constructor, options) => {
        if (
          name &&
          name.startsWith("gmp-") &&
          window.customElements.get(name)
        ) {
          return; // avoid re-definition warnings/errors
        }
        try {
          return originalDefine(name, constructor, options);
        } catch {
          // swallow duplicate define errors safely
          return;
        }
      };
      window.__gmpDefineGuardInstalled = true;
    }
  }, []);

  // (Removed script-present toggle to let LoadScript manage lifecycle)

  // Force use of HashRouter in Electron
  const CustomRouter = ({ children }) => {
    return <HashRouter>{children}</HashRouter>;
  };

  CustomRouter.propTypes = {
    children: PropTypes.node,
  };

  // If offline: render app without Google Maps
  if (!isOnline) {
    return (
      <CustomRouter>
        <LoaderProvider>
          <UserDataProvider>
            <AnimatedRoutes />
            <GlobalErrorHandler />
          </UserDataProvider>
        </LoaderProvider>
      </CustomRouter>
    );
  }

  // If online: wrap the entire app with LoadScript and let it manage readiness
  return (
    <LoadScript
      googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
      id="google-maps-script"
      libraries={googleMapsLibraries}
      loadingElement={<div style={{ display: "none" }}></div>}>
      <CustomRouter>
        <LoaderProvider>
          <UserDataProvider>
            <AnimatedRoutes />
            <GlobalErrorHandler />
          </UserDataProvider>
        </LoaderProvider>
      </CustomRouter>
    </LoadScript>
  );
}

export default App;
